import React, { useRef, useState } from "react";
import { Upload, Loader2 } from "lucide-react";
import { api } from "../../lib/api";
import { formatApiError } from "../../lib/errors";
import { Card, Field, Select, Input, PrimaryButton } from "../UI";
import { todayIso } from "./importShared";

/* LT-19b §11 — upload panel. Supplier selector is a convenience that
 * pre-fills BLANK supplier_name cells; it never overrides a supplier named
 * in the file and never enters `raw`. */
export function ImportUploadPanel({ suppliers, onUploaded }) {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [supplierId, setSupplierId] = useState("");
  const [effectiveFrom, setEffectiveFrom] = useState(todayIso());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!file) { setError("Choose a CSV file first."); return; }
    const fd = new FormData();
    fd.append("file", file, file.name);
    if (supplierId) fd.append("default_supplier_id", supplierId);
    if (effectiveFrom) fd.append("effective_from", effectiveFrom);
    setBusy(true);
    try {
      const r = await api.post("/material-imports", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setFile(null);
      if (fileRef.current) fileRef.current.value = "";
      onUploaded?.(r.data);
    } catch (err) {
      setError(formatApiError(err, "Upload failed"));
    } finally {
      setBusy(false);
    }
  };

  const activeSuppliers = (suppliers || []).filter((s) => s.status !== "archived");
  return (
    <Card className="p-5" allowOverflow>
      <form onSubmit={submit} className="grid gap-4" style={{ gridTemplateColumns: "2fr 1.4fr 1fr auto" }} data-testid="import-upload-panel">
        <Field label="Price list (CSV)" required hint="Excel → Save As → CSV or CSV UTF-8. Both import identically.">
          <input ref={fileRef} type="file" accept=".csv,text/csv" onChange={(e) => setFile(e.target.files?.[0] || null)}
            className="h-9 w-full text-ink-2" style={{ fontSize: 12.5 }} data-testid="import-file-input" />
        </Field>
        <Field label="Supplier (optional)" hint="Pre-fills blank supplier_name cells only. A supplier named in the file always wins.">
          <Select value={supplierId} onChange={setSupplierId} placeholder="— from the file —"
            options={[{ value: "", label: "— from the file —" }, ...activeSuppliers.map((s) => ({ value: s.id, label: s.name }))]}
            data-testid="import-supplier-select" />
        </Field>
        <Field label="Effective from" hint="Prices apply as soon as you import. This date records when the supplier's list was issued.">
          <Input type="date" value={effectiveFrom} onChange={setEffectiveFrom} data-testid="import-effective-from-input" />
        </Field>
        <div className="pt-6">
          <PrimaryButton type="submit" icon={busy ? Loader2 : Upload} disabled={busy || !file} data-testid="import-upload-submit">
            {busy ? "Uploading…" : "Upload & preview"}
          </PrimaryButton>
        </div>
      </form>
      {error && (
        <div className="mt-3 rounded border border-red-200 bg-red-50 text-red-800 px-3 py-2" style={{ fontSize: 12.5 }}
             data-testid="import-upload-error">{error}</div>
      )}
    </Card>
  );
}
